import { useEffect, useRef } from 'react';
import { sendHeightToParent, isInIframe } from '../utils/iframe';

/**
 * Hook to automatically send height updates to parent window when content changes
 */
export const useIframeResize = () => {
  const lastHeightRef = useRef<number>(0);

  useEffect(() => {
    if (!isInIframe()) return;

    const sendHeight = () => {
      const body = document.body;
      const html = document.documentElement;

      // Get the maximum height from various sources
      const height = Math.max(
        body.scrollHeight,
        body.offsetHeight,
        html.clientHeight,
        html.scrollHeight,
        html.offsetHeight
      );

      // Only send if height has changed significantly (avoid spam)
      if (Math.abs(height - lastHeightRef.current) > 10) {
        lastHeightRef.current = height;
        sendHeightToParent(height);
      }
    };

    // Send initial height
    sendHeight();

    // Create ResizeObserver to watch for content changes
    const resizeObserver = new ResizeObserver(() => {
      sendHeight();
    });

    // Observe the body element
    resizeObserver.observe(document.body);

    // Also listen for DOM mutations (for dynamic content)
    const mutationObserver = new MutationObserver(() => {
      sendHeight();
    });

    mutationObserver.observe(document.body, {
      childList: true,
      subtree: true,
      attributes: true,
    });

    // Send height on window resize as well
    window.addEventListener('resize', sendHeight);

    // Cleanup
    return () => {
      resizeObserver.disconnect();
      mutationObserver.disconnect();
      window.removeEventListener('resize', sendHeight);
    };
  }, []);
};
